import React from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import type { PieLabelRenderProps } from "recharts";
import type { ChartType, ColumnDefinition, DataRow } from "../../schema/chart";
import { getDataColumns, getDisplayColumns, getPrimaryKeyColumns } from "./columnHelpers";
import { formatValue, toNumber } from "./formatValue";

const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

interface DataChartProps {
    columns: ColumnDefinition[];
    rows: DataRow[];
    chartType: ChartType;
}

function buildPrimaryKeyLabel(row: DataRow, pkCols: ColumnDefinition[]): string {
    return pkCols.map((col) => formatValue(row[col.name] ?? "", col.dataType)).join(" / ");
}

function yAxisTickFormatter(dataColDefs: ColumnDefinition[]) {
    const firstType = dataColDefs[0]?.dataType;
    if (!firstType) return undefined;
    return (value: number) => formatValue(String(value), firstType);
}

const CustomTooltip: React.FC<{
    active?: boolean;
    payload?: any[];
    label?: string;
    displayCols: ColumnDefinition[];
    dataColDefs: ColumnDefinition[];
}> = ({ active, payload, label, displayCols, dataColDefs }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload?._rawRow as DataRow | undefined;
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 13,
            }}
        >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
            {payload.map((p: any) => {
                const colDef = dataColDefs.find((c) => c.name === p.dataKey);
                const formatted = colDef ? formatValue(String(p.value), colDef.dataType) : p.value;
                return (
                    <div key={p.dataKey}>
                        {p.name}: {formatted}
                    </div>
                );
            })}
            {row &&
                displayCols.map((col) => (
                    <div key={col.name} style={{ color: "#888", marginTop: 2 }}>
                        {col.name}: {formatValue(row[col.name] ?? "", col.dataType)}
                    </div>
                ))}
        </div>
    );
};

export const DataChart: React.FC<DataChartProps> = ({ columns, rows, chartType }) => {
    if (!columns.length || !rows.length) {
        return (
            <div style={{ padding: 32, textAlign: "center", color: "#888" }}>
                No data to display. Add data in the Edit tab.
            </div>
        );
    }

    const pkColDefs = getPrimaryKeyColumns(columns);
    const dataColDefs = getDataColumns(columns);
    const displayColDefs = getDisplayColumns(columns);
    const dataColumnNames = dataColDefs.map((c) => c.name);

    // Build chart-ready data: numeric values for data columns + _label + _rawRow
    const chartData = rows.map((row) => {
        const entry: Record<string, any> = {
            _label: buildPrimaryKeyLabel(row, pkColDefs),
            _rawRow: row,
        };
        for (const col of dataColDefs) {
            entry[col.name] = toNumber(row[col.name] ?? "");
        }
        return entry;
    });

    const yFormatter = yAxisTickFormatter(dataColDefs);

    if (chartType === "PIE") {
        return (
            <PieCharts
                dataColumns={dataColumnNames}
                displayCols={displayColDefs}
                dataColDefs={dataColDefs}
                chartData={chartData}
            />
        );
    }

    if (chartType === "BAR") {
        return (
            <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="_label" />
                    <YAxis tickFormatter={yFormatter} />
                    <Tooltip
                        content={
                            <CustomTooltip displayCols={displayColDefs} dataColDefs={dataColDefs} />
                        }
                    />
                    <Legend />
                    {dataColumnNames.map((col, i) => (
                        <Bar key={col} dataKey={col} fill={COLORS[i % COLORS.length]}>
                            {displayColDefs.length > 0 && (
                                <LabelList
                                    dataKey={displayColDefs[0].name}
                                    position="top"
                                    fontSize={11}
                                />
                            )}
                        </Bar>
                    ))}
                </BarChart>
            </ResponsiveContainer>
        );
    }

    // LINE (default)
    return (
        <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="_label" />
                <YAxis tickFormatter={yFormatter} />
                <Tooltip
                    content={
                        <CustomTooltip displayCols={displayColDefs} dataColDefs={dataColDefs} />
                    }
                />
                <Legend />
                {dataColumnNames.map((col, i) => (
                    <Line
                        key={col}
                        type="monotone"
                        dataKey={col}
                        stroke={COLORS[i % COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                    >
                        {i === 0 && displayColDefs.length > 0 && (
                            <LabelList
                                dataKey={displayColDefs[0].name}
                                position="top"
                                fontSize={11}
                            />
                        )}
                    </Line>
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
};

const PieCharts: React.FC<{
    dataColumns: string[];
    displayCols: ColumnDefinition[];
    dataColDefs: ColumnDefinition[];
    chartData: Record<string, any>[];
}> = ({ dataColumns, displayCols, dataColDefs, chartData }) => {
    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: `repeat(${Math.min(dataColumns.length, 3)}, 1fr)`,
                gap: 16,
            }}
        >
            {dataColumns.map((col, colIdx) => {
                const pieData = chartData.map((entry) => ({
                    name: entry._label as string,
                    value: entry[col] as number,
                    _rawRow: entry._rawRow,
                }));

                return (
                    <div key={col} style={{ textAlign: "center" }}>
                        <h4 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600 }}>{col}</h4>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={100}
                                    label={(props: PieLabelRenderProps) => {
                                        return `${props.name ?? ""} (${(((props.percent as number) ?? 0) * 100).toFixed(0)}%)`;
                                    }}
                                >
                                    {pieData.map((_, i) => (
                                        <Cell
                                            key={`cell-${i}`}
                                            fill={COLORS[(colIdx + i) % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip
                                    content={
                                        <CustomTooltip
                                            displayCols={displayCols}
                                            dataColDefs={dataColDefs}
                                        />
                                    }
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                );
            })}
        </div>
    );
};
